import { useState, useCallback, useRef } from 'react'
import { DateRangeState } from './types'
import { getInitialDateRange, saveDateRangeToStorage } from './utils'

export const useDateRangePicker = (
  initialStartDate?: Date | null,
  initialEndDate?: Date | null,
  timePeriod: 'period' = 'period',
  persistToStorage: boolean = true
) => {
  // Load initial dates from localStorage if no external dates provided
  const getInitialDates = () => {
    if (initialStartDate && initialEndDate) {
      return [initialStartDate, initialEndDate]
    }
    return getInitialDateRange()
  }

  const [startFromStorage, endFromStorage] = getInitialDates()

  const [state, setState] = useState<DateRangeState>({
    isOpen: false,
    selectedStartDate: initialStartDate || startFromStorage,
    selectedEndDate: initialEndDate || endFromStorage,
    selectionType: null,
    tempStartDate: null,
    tempEndDate: null,
    timePeriod,
    isWeekSelectionMode: false
  })

  const anchorRef = useRef<HTMLElement | null>(null)

  const openPicker = useCallback((anchorElement: HTMLElement, type: 'start' | 'end' = 'start') => {
    anchorRef.current = anchorElement
    setState(prev => ({
      ...prev,
      isOpen: true,
      selectionType: type,
      tempStartDate: prev.selectedStartDate,
      tempEndDate: prev.selectedEndDate
    }))
  }, [])

  const closePicker = useCallback(() => {
    setState(prev => ({
      ...prev,
      isOpen: false,
      selectionType: null,
      tempStartDate: null,
      tempEndDate: null,
      isWeekSelectionMode: false
    }))
    anchorRef.current = null
  }, [])

  const selectDate = useCallback((date: Date | null, type: 'start' | 'end') => {
    setState(prev => ({
      ...prev,
      [`selected${type === 'start' ? 'Start' : 'End'}Date`]: date,
      [`temp${type === 'start' ? 'Start' : 'End'}Date`]: date
    }))
  }, [])

  const confirmSelection = useCallback((startDate: Date, endDate: Date) => {
    setState(prev => ({
      ...prev,
      selectedStartDate: startDate,
      selectedEndDate: endDate,
      tempStartDate: null,
      tempEndDate: null
    }))

    // Save to shared localStorage (페이지별 독립 사용 시 persistToStorage=false로 비활성화)
    if (persistToStorage) {
      saveDateRangeToStorage(startDate, endDate)
    }
  }, [persistToStorage])

  const updateTimePeriod = useCallback((newTimePeriod: 'period') => {
    setState(prev => ({
      ...prev,
      timePeriod: newTimePeriod
    }))
  }, [])

  const setDateRange = useCallback((startDate: Date | null, endDate: Date | null) => {
    setState(prev => ({
      ...prev,
      selectedStartDate: startDate,
      selectedEndDate: endDate
    }))
  }, [])

  const setWeekSelectionMode = useCallback((isWeekMode: boolean) => {
    setState(prev => ({
      ...prev,
      isWeekSelectionMode: isWeekMode
    }))
  }, [])

  return {
    state,
    anchorElement: anchorRef.current,
    openPicker,
    closePicker,
    selectDate,
    confirmSelection,
    updateTimePeriod,
    setDateRange,
    setWeekSelectionMode
  }
}